import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { dashboardAPI, casesAPI, organizationsAPI, adminAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  FileText, 
  Clock, 
  CheckCircle, 
  Plus, 
  Download,
  Loader2,
  Building2,
  BarChart3,
  Receipt,
  BookOpen,
  HelpCircle,
  Search,
  CreditCard,
  ExternalLink,
  AlertCircle,
  ShieldCheck,
  Lock,
  Crown,
  ScanLine,
  Upload,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';

export default function DashboardPage() {
  const { user, isSuperAdmin, organizationName, organizationId } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentCases, setRecentCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [encryptionStatus, setEncryptionStatus] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [orgFeaturesList, setOrgFeaturesList] = useState([]);
  const [featuresLoading, setFeaturesLoading] = useState(false);
  const [togglingFeature, setTogglingFeature] = useState(null);

  useEffect(() => {
    fetchData();
    if (!isSuperAdmin && organizationId) {
      fetchStripeStatus();
    }
    if (isSuperAdmin) {
      fetchEncryptionStatus();
      fetchOrgFeatures();
    }
  }, []);

  const fetchStripeStatus = async () => {
    try {
      const response = await organizationsAPI.getStripeConnectStatus(organizationId);
      setStripeStatus(response.data);
    } catch { /* silently fail */ }
  };

  const handleStripeConnect = async () => {
    setStripeLoading(true);
    try {
      const response = await organizationsAPI.initiateStripeConnect(organizationId, window.location.origin);
      if (response.data.onboarding_url) {
        window.location.href = response.data.onboarding_url;
      } else if (response.data.status === 'active') {
        toast.success('Already connected to Stripe');
        fetchStripeStatus();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate Stripe Connect');
    } finally {
      setStripeLoading(false);
    }
  };

  const handleStripeDashboard = async () => {
    try {
      const response = await organizationsAPI.getStripeDashboard(organizationId);
      if (response.data.dashboard_url) {
        window.open(response.data.dashboard_url, '_blank');
      }
    } catch {
      toast.error('Failed to open Stripe dashboard');
    }
  };

  const fetchEncryptionStatus = async () => {
    try {
      const response = await adminAPI.getEncryptionStatus();
      setEncryptionStatus(response.data);
    } catch { /* silently fail */ }
  };

  const fetchOrgFeatures = async () => {
    setFeaturesLoading(true);
    try {
      const response = await organizationsAPI.getAllFeaturesSummary();
      setOrgFeaturesList(response.data);
    } catch { /* silently fail */ }
    setFeaturesLoading(false);
  };

  const handleToggleFeature = async (orgId, featureName, currentValue) => {
    const key = `${orgId}-${featureName}`;
    setTogglingFeature(key);
    try {
      const org = orgFeaturesList.find(o => o.id === orgId);
      const updated = { ...org.features, [featureName]: !currentValue };
      await organizationsAPI.updateFeatures(orgId, updated);
      setOrgFeaturesList(prev => prev.map(o => o.id === orgId ? { ...o, features: updated } : o));
      const featureLabels = { form_scanner: 'Form Scanner', batch_scanner: 'Batch Scanner', online_filing: 'eFiling', trial_mode: 'Trial Mode' };
      toast.success(`${featureLabels[featureName] || featureName} ${!currentValue ? 'enabled' : 'disabled'} for ${org.name}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update feature');
    }
    setTogglingFeature(null);
  };

  const handleMigration = async () => {
    setMigrating(true);
    try {
      const response = await adminAPI.runEncryptionMigration();
      toast.success(response.data.message);
      fetchEncryptionStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Migration failed');
    } finally {
      setMigrating(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, casesRes] = await Promise.all([
        dashboardAPI.getStats(),
        casesAPI.getAll()
      ]);
      setStats(statsRes.data);
      setRecentCases(casesRes.data.slice(0, 5));
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await casesAPI.exportCSV();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cases_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export completed');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Active': 'bg-success text-white',
      'Pending': 'bg-warning text-white',
      'Approved': 'bg-primary text-white',
      'Denied': 'bg-error text-white',
    };
    return colors[status] || 'bg-muted text-foreground';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />

      {/* Main Content */}
      <main className="ml-0 md:ml-64 p-4 md:p-8 pt-16 md:pt-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-xl sm:text-2xl font-bold text-foreground tracking-tight">
              Dashboard
            </h1>
            <p className="font-body text-sm text-muted-foreground">
              {isSuperAdmin 
                ? `Welcome back, ${user?.name?.split(' ')[0]}` 
                : `Welcome back to ${organizationName}`}
            </p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <Button 
              variant="outline" 
              className="font-body"
              onClick={handleExport}
              data-testid="export-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Link to="/cases/new">
              <Button className="btn-lift bg-primary text-white font-body" data-testid="add-case-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Case
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 animate-stagger">
          <Card className="card-hover" data-testid="stat-total-cases">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="font-body text-sm font-medium text-muted-foreground">
                Total Cases
              </CardTitle>
              <FileText className="w-5 h-5 text-primary" strokeWidth={1.5} />
            </CardHeader>
            <CardContent>
              <p className="font-heading text-3xl font-bold text-foreground">
                {stats?.total_cases || 0}
              </p>
            </CardContent>
          </Card>

          <Card className="card-hover" data-testid="stat-active-cases">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="font-body text-sm font-medium text-muted-foreground">
                Active Cases
              </CardTitle>
              <Clock className="w-5 h-5 text-warning" strokeWidth={1.5} />
            </CardHeader>
            <CardContent>
              <p className="font-heading text-3xl font-bold text-foreground">
                {stats?.active_cases || 0}
              </p>
            </CardContent>
          </Card>

          <Card className="card-hover" data-testid="stat-approved">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="font-body text-sm font-medium text-muted-foreground">
                Approved Cases
              </CardTitle>
              <CheckCircle className="w-5 h-5 text-success" strokeWidth={1.5} />
            </CardHeader>
            <CardContent>
              <p className="font-heading text-3xl font-bold text-foreground">
                {stats?.approved_cases || 0}
              </p>
            </CardContent>
          </Card>

          {isSuperAdmin ? (
            <Card className="card-hover" data-testid="stat-organizations">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="font-body text-sm font-medium text-muted-foreground">
                  Organizations
                </CardTitle>
                <Building2 className="w-5 h-5 text-accent" strokeWidth={1.5} />
              </CardHeader>
              <CardContent>
                <p className="font-heading text-3xl font-bold text-foreground">
                  {stats?.total_organizations || 0}
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card className="card-hover" data-testid="stat-documents">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="font-body text-sm font-medium text-muted-foreground">
                  Documents
                </CardTitle>
                <FileText className="w-5 h-5 text-accent" strokeWidth={1.5} />
              </CardHeader>
              <CardContent>
                <p className="font-heading text-3xl font-bold text-foreground">
                  {stats?.documents_uploaded || 0}
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Recent Cases - Only show for org admins/users, not super admins */}
        {!isSuperAdmin && (
          <Card data-testid="recent-cases-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg font-semibold">
                Recent Cases
              </CardTitle>
              <Link to="/cases">
                <Button variant="ghost" size="sm" className="font-body text-primary" data-testid="view-all-cases-btn">
                  View All
                  <Search className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {recentCases.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
                  <p className="font-body text-muted-foreground">No cases yet</p>
                  <Link to="/cases/new">
                    <Button className="mt-4 btn-lift bg-primary text-white font-body" data-testid="add-first-case-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Your First Case
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {recentCases.map((caseItem) => (
                    <Link 
                      key={caseItem.id} 
                      to={`/cases/${caseItem.id}`}
                      className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-secondary/50 transition-colors"
                      data-testid={`recent-case-${caseItem.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="font-heading font-semibold text-primary">
                            {caseItem.name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <div>
                          <p className="font-body font-medium text-foreground">{caseItem.name}</p>
                          <p className="font-body text-sm text-muted-foreground">
                            {caseItem.type_of_services || 'No service type'}
                          </p>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-body font-medium ${getStatusColor(caseItem.status)}`}>
                        {caseItem.status}
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Quick Actions for Super Admin */}
        {isSuperAdmin && (
          <Card data-testid="super-admin-actions-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg font-semibold">
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Link to="/admin/organizations">
                  <div className="p-4 rounded-lg border border-border hover:bg-secondary/50 transition-colors flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-body font-medium text-foreground">Manage Organizations</p>
                      <p className="font-body text-sm text-muted-foreground">View and create orgs</p>
                    </div>
                  </div>
                </Link>
                <Link to="/invoices">
                  <div className="p-4 rounded-lg border border-border hover:bg-secondary/50 transition-colors flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                      <Receipt className="w-5 h-5 text-accent" />
                    </div>
                    <div>
                      <p className="font-body font-medium text-foreground">Invoices</p>
                      <p className="font-body text-sm text-muted-foreground">Billing & payments</p>
                    </div>
                  </div>
                </Link>
                <Link to="/reports">
                  <div className="p-4 rounded-lg border border-border hover:bg-secondary/50 transition-colors flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-success/10 flex items-center justify-center">
                      <BarChart3 className="w-5 h-5 text-success" />
                    </div>
                    <div>
                      <p className="font-body font-medium text-foreground">Reports</p>
                      <p className="font-body text-sm text-muted-foreground">Analytics & exports</p>
                    </div>
                  </div>
                </Link>
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/QUICK_START.html`} target="_blank" rel="noopener noreferrer">
                  <div className="p-4 rounded-lg border border-border hover:bg-secondary/50 transition-colors flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                      <BookOpen className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-body font-medium text-foreground">Help & Guides</p>
                      <p className="font-body text-sm text-muted-foreground">Documentation</p>
                    </div>
                  </div>
                </a>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Data Encryption Card - Super Admin Only */}
        {isSuperAdmin && encryptionStatus && (
          <Card data-testid="encryption-status-card" className="mt-6">
            <CardHeader>
              <CardTitle className="font-heading text-lg font-semibold flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-primary" />
                Data Encryption
              </CardTitle>
            </CardHeader>
            <CardContent>
              {encryptionStatus.fully_encrypted ? (
                <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Lock className="w-6 h-6 text-green-600" />
                    <div>
                      <p className="font-body font-medium text-green-800">All Data Encrypted</p>
                      <p className="font-body text-sm text-green-700">
                        {encryptionStatus.total_cases} cases &middot; Fields: {encryptionStatus.encrypted_fields.join(', ')}
                      </p>
                    </div>
                  </div>
                  <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">AES-256</span>
                </div>
              ) : (
                <div className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-6 h-6 text-amber-600" />
                    <div>
                      <p className="font-body font-medium text-amber-800">
                        {encryptionStatus.unencrypted_in_sample} of {encryptionStatus.sample_size} cases unencrypted
                      </p>
                      <p className="font-body text-sm text-amber-700">
                        Run migration to encrypt: {encryptionStatus.encrypted_fields.join(', ')}
                      </p>
                    </div>
                  </div>
                  <Button size="sm" onClick={handleMigration} disabled={migrating} data-testid="encrypt-migrate-btn">
                    {migrating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
                    Encrypt All
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Stripe Connect Card - For Org Admins */}
        {!isSuperAdmin && organizationId && (
          <Card data-testid="stripe-connect-card" className="mt-6">
            <CardHeader>
              <CardTitle className="font-heading text-lg font-semibold flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-primary" />
                Payment Setup
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stripeStatus?.status === 'active' ? (
                <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                    <div>
                      <p className="font-body font-medium text-green-800">Stripe Connected</p>
                      <p className="font-body text-sm text-green-700">Client payments are routed directly to your Stripe account (5% platform fee)</p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={handleStripeDashboard} data-testid="org-stripe-dashboard-btn">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Stripe Dashboard
                  </Button>
                </div>
              ) : stripeStatus?.status === 'stripe_not_configured' ? (
                <div className="flex items-center gap-3 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                  <AlertCircle className="w-6 h-6 text-slate-500" />
                  <div>
                    <p className="font-body font-medium text-slate-700">Payment Setup Not Available</p>
                    <p className="font-body text-sm text-slate-600">The platform needs to configure Stripe Connect. Contact your administrator.</p>
                  </div>
                </div>
              ) : stripeStatus?.status === 'pending_verification' || stripeStatus?.status === 'onboarding_incomplete' ? (
                <div className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-6 h-6 text-amber-600" />
                    <div>
                      <p className="font-body font-medium text-amber-800">
                        {stripeStatus.status === 'pending_verification' ? 'Verification Pending' : 'Onboarding Incomplete'}
                      </p>
                      <p className="font-body text-sm text-amber-700">
                        {stripeStatus.status === 'pending_verification' 
                          ? 'Stripe is reviewing your account. This usually takes 1-2 days.'
                          : 'Please complete the Stripe onboarding to start receiving payments.'}
                      </p>
                    </div>
                  </div>
                  {stripeStatus.status === 'onboarding_incomplete' && (
                    <Button variant="outline" size="sm" onClick={handleStripeConnect} disabled={stripeLoading} data-testid="org-stripe-resume-btn">
                      {stripeLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
                      Resume Setup
                    </Button>
                  )}
                </div>
              ) : (
                <div className="text-center py-6">
                  <CreditCard className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="font-body font-medium text-foreground mb-1">Connect Your Stripe Account</p>
                  <p className="font-body text-sm text-muted-foreground mb-4 max-w-md mx-auto">
                    Set up Stripe to receive client invoice payments directly to your bank account. NewSlate takes a 5% platform fee.
                  </p>
                  <Button onClick={handleStripeConnect} disabled={stripeLoading} data-testid="org-stripe-connect-btn">
                    {stripeLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
                    Connect Stripe Account
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Feature Management - Super Admin Only */}
        {isSuperAdmin && (
          <Card className="mt-6" data-testid="feature-management-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg font-semibold flex items-center gap-2">
                <Crown className="w-5 h-5 text-amber-500" />
                Feature Management
              </CardTitle>
              <p className="font-body text-sm text-muted-foreground">Enable or disable premium features per organization</p>
            </CardHeader>
            <CardContent>
              {featuresLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : orgFeaturesList.length === 0 ? (
                <p className="font-body text-sm text-muted-foreground py-4 text-center">No active organizations found</p>
              ) : (
                <div className="space-y-3">
                  {/* Header */}
                  <div className="grid grid-cols-[1fr_110px_110px_110px_140px] gap-4 px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    <span>Organization</span>
                    <span className="text-center">Form Scanner</span>
                    <span className="text-center">Batch Scanner</span>
                    <span className="text-center">eFiling</span>
                    <span className="text-center">Trial Mode</span>
                  </div>
                  {/* Rows */}
                  {orgFeaturesList.map(org => (
                    <div key={org.id} className="grid grid-cols-[1fr_110px_110px_110px_140px] gap-4 items-center px-4 py-3 rounded-lg border border-border hover:bg-secondary/30 transition-colors" data-testid={`feature-row-${org.id}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <Building2 className="w-4 h-4 text-primary" />
                        </div>
                        <span className="font-body text-sm font-medium text-foreground truncate">{org.name}</span>
                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <Switch
                          checked={!!org.features?.form_scanner}
                          onCheckedChange={() => handleToggleFeature(org.id, 'form_scanner', !!org.features?.form_scanner)}
                          disabled={togglingFeature === `${org.id}-form_scanner`}
                          data-testid={`toggle-form-scanner-${org.id}`}
                        />
                        <Badge variant={org.features?.form_scanner ? 'default' : 'secondary'} className={`text-[10px] px-1.5 ${org.features?.form_scanner ? 'bg-green-600 hover:bg-green-600' : ''}`}>
                          {org.features?.form_scanner ? 'On' : 'Off'}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <Switch
                          checked={!!org.features?.batch_scanner}
                          onCheckedChange={() => handleToggleFeature(org.id, 'batch_scanner', !!org.features?.batch_scanner)}
                          disabled={togglingFeature === `${org.id}-batch_scanner`}
                          data-testid={`toggle-batch-scanner-${org.id}`}
                        />
                        <Badge variant={org.features?.batch_scanner ? 'default' : 'secondary'} className={`text-[10px] px-1.5 ${org.features?.batch_scanner ? 'bg-green-600 hover:bg-green-600' : ''}`}>
                          {org.features?.batch_scanner ? 'On' : 'Off'}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <Switch
                          checked={!!org.features?.online_filing}
                          onCheckedChange={() => handleToggleFeature(org.id, 'online_filing', !!org.features?.online_filing)}
                          disabled={togglingFeature === `${org.id}-online_filing`}
                          data-testid={`toggle-online-filing-${org.id}`}
                        />
                        <Badge variant={org.features?.online_filing ? 'default' : 'secondary'} className={`text-[10px] px-1.5 ${org.features?.online_filing ? 'bg-green-600 hover:bg-green-600' : ''}`}>
                          {org.features?.online_filing ? 'On' : 'Off'}
                        </Badge>
                      </div>
                      <div className="flex flex-col items-center gap-1">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={!!org.features?.trial_mode}
                            onCheckedChange={() => handleToggleFeature(org.id, 'trial_mode', !!org.features?.trial_mode)}
                            disabled={togglingFeature === `${org.id}-trial_mode`}
                            data-testid={`toggle-trial-mode-${org.id}`}
                          />
                          <Badge variant={org.features?.trial_mode ? 'default' : 'secondary'} className={`text-[10px] px-1.5 ${org.features?.trial_mode ? 'bg-amber-600 hover:bg-amber-600' : ''}`}>
                            {org.features?.trial_mode ? 'On' : 'Off'}
                          </Badge>
                        </div>
                        {org.features?.trial_mode && org.trial_info && (
                          <span className={`text-[9px] font-medium ${org.trial_info.expired ? 'text-red-500' : 'text-muted-foreground'}`}>
                            {org.trial_info.expired ? 'Expired' : `${org.trial_info.days_left}d left · ${org.trial_info.cases_used}/10`}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Help Documentation Card - Available to all users */}
        <Card className="mt-6" data-testid="help-docs-card">
          <CardHeader>
            <CardTitle className="font-heading text-lg font-semibold flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-primary" />
              Help & Documentation
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <a 
                href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/ONBOARDING_GUIDE.html`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="block p-4 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-colors"
                data-testid="help-onboarding-guide"
              >
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl">🚀</span>
                  <h4 className="font-body font-semibold text-foreground">Onboarding Guide</h4>
                </div>
                <p className="font-body text-sm text-muted-foreground">Welcome guide with demo scenarios and onboarding checklists.</p>
              </a>
              <a 
                href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/QUICK_START.html`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="block p-4 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-colors"
                data-testid="help-quick-start"
              >
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl">⚡</span>
                  <h4 className="font-body font-semibold text-foreground">Quick Start Guide</h4>
                </div>
                <p className="font-body text-sm text-muted-foreground">Get up and running in minutes with step-by-step setup instructions.</p>
              </a>
              <a 
                href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/USER_GUIDE.html`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="block p-4 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-colors"
                data-testid="help-user-guide"
              >
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl">📖</span>
                  <h4 className="font-body font-semibold text-foreground">User Guide</h4>
                </div>
                <p className="font-body text-sm text-muted-foreground">Complete guide to all features including cases, portals, and invoicing.</p>
              </a>
              <a 
                href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/NewSlate_Training_Manual.html`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="block p-4 rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-colors"
                data-testid="help-training-manual"
              >
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl">📘</span>
                  <h4 className="font-body font-semibold text-foreground">Training Manual</h4>
                </div>
                <p className="font-body text-sm text-muted-foreground">Comprehensive training manual for new staff and administrators.</p>
              </a>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
